/**
 * Core pricing tier definitions for Zeytune.
 *
 * To add a new tier:
 *   1. Add its id to the `PricingTier` union type.
 *   2. Add its definition to `PRICING_TIERS`.
 *   3. Update the access records for relevant features in `features.ts`.
 */

/** Union of all known pricing tier identifiers. */
export type PricingTier = 'free' | 'pro' | 'teams';

/** Metadata describing a single pricing tier. */
export interface TierDefinition {
    /** Unique identifier — must match the `PricingTier` key. */
    id: PricingTier;

    /** Human-readable display name shown in the UI. */
    name: string;

    /**
     * Numeric rank used for tier comparisons.
     * Higher rank = higher / more permissive tier (teams > pro > free).
     */
    rank: number;
}

/** Single source of truth for all available pricing tiers. */
export const PRICING_TIERS: Record<PricingTier, TierDefinition> = {
    free: { id: 'free', name: 'Free', rank: 0 },
    pro: { id: 'pro', name: 'Pro', rank: 1 },
    teams: { id: 'teams', name: 'Teams', rank: 2 },
};

/**
 * Pricing-related numeric limits. Keep limits near pricing logic so they're
 * discoverable by pricing-related code and UIs.
 */
export const INSIGHT_LIMIT = 100;
export const FREE_OST_LIMIT = 2;
/** Projects a free-tier account may own. Mirrored server-side in server.js's PLAN_LIMITS. */
export const FREE_PROJECT_LIMIT = 2;
/** Nodes (outcome + opportunities + solutions + stories) a free-tier account may have per tree. Mirrored server-side in lib/planLimits.js's PLAN_LIMITS. */
export const FREE_NODE_LIMIT = 30;

/**
 * Returns true if `candidate` meets or exceeds the `required` tier.
 * Useful for "at least Pro" style checks without enumerating every tier.
 */
export function isTierAtLeast(candidate: PricingTier, required: PricingTier): boolean {
    return PRICING_TIERS[candidate].rank >= PRICING_TIERS[required].rank;
}
